import { useEffect, useState } from 'react';

const STORAGE_PREFIX = 'sidebarSettings';

interface SidebarSettings {
  order: string[];
  hidden: string[];
}

const EMPTY_SETTINGS: SidebarSettings = { order: [], hidden: [] };

function getStorageKey(storageSuffix?: string) {
  return storageSuffix ? `${STORAGE_PREFIX}:${storageSuffix}` : STORAGE_PREFIX;
}

function loadSettings(storageSuffix?: string): SidebarSettings {
  try {
    const raw = localStorage.getItem(getStorageKey(storageSuffix));
    if (!raw) return EMPTY_SETTINGS;
    const parsed = JSON.parse(raw);
    return {
      order: Array.isArray(parsed.order) ? parsed.order : [],
      hidden: Array.isArray(parsed.hidden) ? parsed.hidden : [],
    };
  } catch {
    return EMPTY_SETTINGS;
  }
}

/**
 * `storageSuffix` (e.g. current user id) scopes customization per user so that
 * one user's saved layout/hidden items never carry over to another user's
 * permission-filtered menu on a shared browser.
 */
export function useSidebarSettings<T extends { code: string }>(
  defaultItems: T[],
  storageSuffix?: string,
) {
  const [settings, setSettings] = useState<SidebarSettings>(() => loadSettings(storageSuffix));

  useEffect(() => {
    setSettings(loadSettings(storageSuffix));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageSuffix]);

  useEffect(() => {
    localStorage.setItem(getStorageKey(storageSuffix), JSON.stringify(settings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, storageSuffix]);

  const defaultCodes = defaultItems.map((item) => item.code);
  const known = settings.order.filter((code) => defaultCodes.includes(code));
  const missing = defaultCodes.filter((code) => !known.includes(code));
  const orderedCodes = [...known, ...missing];

  const byCode = new Map(defaultItems.map((item) => [item.code, item]));
  const orderedItems = orderedCodes.map((code) => byCode.get(code)).filter(Boolean) as T[];
  const visibleItems = orderedItems.filter((item) => !settings.hidden.includes(item.code));

  const toggleHidden = (code: string) => {
    setSettings((prev) => ({
      ...prev,
      hidden: prev.hidden.includes(code)
        ? prev.hidden.filter((c) => c !== code)
        : [...prev.hidden, code],
    }));
  };

  const reorder = (fromCode: string, toCode: string) => {
    if (fromCode === toCode) return;
    const fromIndex = orderedCodes.indexOf(fromCode);
    const toIndex = orderedCodes.indexOf(toCode);
    if (fromIndex === -1 || toIndex === -1) return;
    const next = [...orderedCodes];
    next.splice(fromIndex, 1);
    next.splice(toIndex, 0, fromCode);
    setSettings((prev) => ({ ...prev, order: next }));
  };

  const resetSettings = () => setSettings(EMPTY_SETTINGS);

  return {
    orderedItems,
    visibleItems,
    hidden: settings.hidden,
    toggleHidden,
    reorder,
    resetSettings,
  };
}
